/**
 * Row keys for the v1.5 directory.
 *
 * `DirectoryItem.id` is a **string** and every Birtvy item id is a **number**, so a key has to carry
 * the kind as well as the number: `slide:7` and `doc:7` are different objects, and the canvas's single
 * `idRef` counter means only one of them can exist at a time — but nothing stops a deck holding
 * `slide:7` today and `doc:7` after a delete, so the kind is not decoration.
 *
 * The prefixes are deliberately multi-letter. `birtvyItems.ts` already spends the single letters on its
 * own item-row convention (`s` slides, `d` docs, `n` notes, `i` images, **`w` wires**, `g` groups,
 * `ds` design systems), and a `w:3` that means a website here and a wire there is the kind of thing
 * that reads fine for a year and then costs a day.
 */
import type { DirectoryKind } from '~/design-system/components/v1_5/Directory';

const PREFIX: Record<DirectoryKind, string> = {
  artifacts: 'slide',
  documents: 'doc',
  images: 'img',
  websites: 'web',
};

const KIND_OF: Record<string, DirectoryKind> = {
  slide: 'artifacts',
  doc: 'documents',
  img: 'images',
  web: 'websites',
};

/** `('artifacts', 7)` → `'slide:7'`. */
export function itemKey(kind: DirectoryKind, id: number): string {
  return `${PREFIX[kind]}:${id}`;
}

/** `'slide:7'` → `{ kind: 'artifacts', id: 7 }`. `null` for anything this module did not write. */
export function parseItemKey(key: string): { kind: DirectoryKind; id: number } | null {
  const at = key.indexOf(':');
  if (at === -1) return null;
  const kind = KIND_OF[key.slice(0, at)];
  if (!kind) return null;
  const id = Number.parseInt(key.slice(at + 1), 10);
  return Number.isFinite(id) ? { kind, id } : null;
}
